import { zodResolver } from "@hookform/resolvers/zod";
import Editor from "@monaco-editor/react";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import React from "react";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import type { z } from "zod";

import * as schemas from "@blis/shared/schemas/process";
import { trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import { Heading } from "@/components/ui/heading";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { ProcessTemplateDetail } from "../../types";

type CreateTemplateFormValues = z.infer<typeof schemas.createTemplate>;
type UpdateVersionFormValues = z.infer<typeof schemas.updateVersion>;

export function ProcessDetailForm({
  template,
}: {
  template?: ProcessTemplateDetail;
}) {
  if (template) {
    return <UpdateVersionForm template={template} />;
  }
  return <CreateTemplateForm />;
}

function CreateTemplateForm() {
  const navigate = useNavigate();

  const createTemplate = trpc.admin.process.createTemplate.useMutation(
    mutationResponse({
      errorTitle: "Failed to create process template",
      successMessage: "Process template created",
      onSuccess: (data) => {
        navigate({
          to: "/admin/processes/$id",
          params: { id: data.id },
          replace: true,
        });
      },
    }),
  );

  const form = useForm<CreateTemplateFormValues>({
    defaultValues: {
      name: "",
      description: "",
      definition: {},
    },
    resolver: zodResolver(schemas.createTemplate),
  });

  const onSubmit: SubmitHandler<CreateTemplateFormValues> = (values) => {
    createTemplate.mutate(values);
  };

  const hasUnsavedChanges = form.formState.isDirty;

  useBlocker({
    shouldBlockFn: () => {
      if (hasUnsavedChanges) {
        const shouldLeave = confirm("Are you sure you want to leave?");
        return !shouldLeave;
      }
      return false;
    },
  });

  const handleSave = () => {
    form.handleSubmit(onSubmit)();
  };

  return (
    <FormProvider {...form}>
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 space-y-4">
          <Heading level={2}>Create Process Template</Heading>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              control={form.control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  isInvalid={!!error}
                  validationBehavior="aria"
                >
                  <Label>Template Name</Label>
                  <Input placeholder="Enter template name" />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />

            <Controller
              control={form.control}
              name="description"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  isInvalid={!!error}
                  validationBehavior="aria"
                >
                  <Label>Description</Label>
                  <Input placeholder="Enter template description" />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
          </div>

          <div className="flex items-center justify-between">
            <Heading level={3}>Definition</Heading>

            {hasUnsavedChanges && (
              <Badge variant="outline" className="text-amber-700">
                • Unsaved changes
              </Badge>
            )}
          </div>
        </div>

        <EditorField />

        <div className="flex-shrink-0 flex items-center justify-end gap-3 mt-4 pt-4 border-t">
          <Button
            onPress={handleSave}
            isDisabled={createTemplate.isPending || !form.formState.isValid}
            isPending={createTemplate.isPending}
          >
            Create Template
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}

function UpdateVersionForm({ template }: { template: ProcessTemplateDetail }) {
  const [versionId, setVersionId] = React.useState<number | undefined>(
    template.versions[0]?.id,
  );

  const templateVersion = trpc.admin.process.getVersion.useQuery(
    {
      id: versionId ?? 0,
    },
    { enabled: !!versionId },
  );

  // Don't render until we have data
  if (!versionId || templateVersion.isLoading || !templateVersion.data) {
    return (
      <div className="flex items-center justify-center h-full">
        <Text>Loading...</Text>
      </div>
    );
  }

  // Parse the definition
  let parsedDefinition = {};
  if (templateVersion.data.definition) {
    if (typeof templateVersion.data.definition === "string") {
      try {
        parsedDefinition = JSON.parse(templateVersion.data.definition);
      } catch {
        parsedDefinition = {};
      }
    } else {
      parsedDefinition = templateVersion.data.definition;
    }
  }

  return (
    <UpdateVersionFormInner
      key={versionId} // Force remount when version changes
      template={template}
      versionId={versionId}
      setVersionId={setVersionId}
      initialDefinition={parsedDefinition}
      versionData={templateVersion.data}
    />
  );
}

function UpdateVersionFormInner({
  template,
  versionId,
  setVersionId,
  initialDefinition,
  versionData,
}: {
  template: ProcessTemplateDetail;
  versionId: number;
  setVersionId: (id: number) => void;
  // biome-ignore lint/suspicious/noExplicitAny: I don't know
  initialDefinition: any;
  // biome-ignore lint/suspicious/noExplicitAny: I don't know
  versionData: any;
}) {
  const updateVersion = trpc.admin.process.updateVersion.useMutation(
    mutationResponse({
      errorTitle: "Failed to update process template version",
      successMessage: "Process template version updated",
    }),
  );

  const form = useForm<UpdateVersionFormValues>({
    defaultValues: {
      id: versionId,
      definition: initialDefinition,
    },
    resolver: zodResolver(schemas.updateVersion),
  });

  const onSubmit: SubmitHandler<UpdateVersionFormValues> = (values) => {
    updateVersion.mutate(values);
  };

  const hasUnsavedChanges = form.formState.isDirty;

  useBlocker({
    shouldBlockFn: () => {
      if (hasUnsavedChanges) {
        const shouldLeave = confirm("Are you sure you want to leave?");
        return !shouldLeave;
      }
      return false;
    },
  });

  const handleSave = () => {
    form.handleSubmit(onSubmit)();
  };

  const currentVersion = template.versions.find((v) => v.id === versionId);

  return (
    <FormProvider {...form}>
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 space-y-4">
          {template.description && (
            <Text className="text-gray-600">{template.description}</Text>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heading level={3}>Version</Heading>

              <Select
                aria-label="Select version"
                placeholder="Select version"
                className="min-w-32"
                selectedKey={versionId}
                onSelectionChange={(key) => setVersionId(key as number)}
              >
                <SelectTrigger />
                <SelectList items={template.versions ?? []}>
                  {(item) => (
                    <SelectItem
                      id={item.id}
                      textValue={`Version ${item.version}`}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span>Version {item.version}</span>
                        {item.id === template.versions[0]?.id && (
                          <Badge variant="soft" className="ml-2">
                            Latest
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  )}
                </SelectList>
              </Select>
            </div>

            {hasUnsavedChanges && (
              <Badge variant="outline" className="text-amber-700">
                • Unsaved changes
              </Badge>
            )}
          </div>
        </div>

        <EditorField />

        <div className="flex-shrink-0 flex items-center justify-between mt-4 pt-4 border-t">
          <div className="text-sm text-gray-600">
            {currentVersion && (
              <span>Editing Version {currentVersion.version}</span>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onPress={() => {
                // TODO: This will eventually create a new version instead of updating
                // console.log("Create new version (not implemented yet)");
              }}
              isDisabled={true}
            >
              Create New Version
            </Button>
            <Button
              onPress={handleSave}
              isDisabled={updateVersion.isPending || !form.formState.isValid}
              isPending={updateVersion.isPending}
            >
              Update Version
            </Button>
          </div>
        </div>
      </div>
    </FormProvider>
  );
}

export function EditorField() {
  const { control, watch } = useFormContext();
  const watchedDefinition = watch("definition");

  const [localValue, setLocalValue] = React.useState(() =>
    JSON.stringify(watchedDefinition || {}, null, 2),
  );

  // Update local value when the watched field changes (e.g., version switching)
  React.useEffect(() => {
    setLocalValue(JSON.stringify(watchedDefinition || {}, null, 2));
  }, [watchedDefinition]);

  return (
    <div className="flex-1 mt-4 border rounded-md overflow-hidden">
      <Controller
        control={control}
        name="definition"
        rules={{
          validate: (value) => {
            // If we have a string, it means JSON parsing failed
            if (typeof value === "string") {
              return "Please fix JSON syntax errors before saving";
            }
            return true;
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <div className="flex flex-col h-full">
            <Editor
              language="json"
              value={localValue}
              onChange={(value) => {
                setLocalValue(value || "");
                try {
                  const parsed = JSON.parse(value || "{}");
                  field.onChange(parsed);
                } catch {
                  // Invalid JSON - store the string so validation fails
                  field.onChange(value || "");
                }
              }}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 11,
                tabSize: 2,
                insertSpaces: true,
                automaticLayout: true,
                formatOnPaste: true,
                formatOnType: false,
                wordWrap: "on",
              }}
            />
            {error && (
              <div className="bg-red-50 border-t border-red-200 text-red-700 text-sm p-3">
                {error.message}
              </div>
            )}
          </div>
        )}
      />
    </div>
  );
}
